import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Typography,
} from '@mui/material';
import { Error, Info } from '@mui/icons-material';
import { getBrokenPoints } from '../../redux/selectors/ingestStats';
import { getEquipment as getEquipFromState } from '../../redux/selectors/equipment';
import { dateTime, displayDuration } from '../../utils/parsing';
import { Row, Fill } from '../util/Page';
import EquipSingleLink from '../explore/EquipSingle/EquipSingleLink';

import './building-points.css';

const status = (variant) => (
  <span className={`status-${variant}`}>
    <span className="icon">{variant === 'ERROR' ? <Error /> : <Info />}</span>
    <span>Onboard alerted to {variant === 'ERROR' ? 'Possible Error' : 'Stale Reading'}</span>
  </span>
);

const INITIAL_PER_PAGE = 10;
const OFFLINE_INGEST_CUTOFF_MS = 60 * 60 * 1000; // offline when no ingest for > 1 hour

const PointRow = ({ point, variant, pointTypes, building, getEquipment }) => (
  <TableRow>
    <TableCell>
      <EquipSingleLink pointId={point.id} text={point.name} />
    </TableCell>
    <TableCell>{getEquipment(point.equipId).display.fullName}</TableCell>
    <TableCell>{pointTypes[point.pointTypeId]?.name}</TableCell>
    <TableCell>{dateTime(point.lastUpdatedTs, building.timezone)}</TableCell>
    <TableCell>{point.value}</TableCell>
    <TableCell>{status(variant)}</TableCell>
  </TableRow>
);

const BuildingPoints = ({
  building,
  brokenPoints,
  percentOnline,
  stalePoints,
  pointTypes,
  isOffline,
  lastIngestDelta,
  getEquipment,
}) => {
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(INITIAL_PER_PAGE);
  // only show stale rows if the building isn't offline (don't want to show every point as stale)
  const staleRows = isOffline ? [] : stalePoints.map((p) => ({ ...p, variant: 'STALE' }));
  const allRows = brokenPoints.map((p) => ({ ...p, variant: 'ERROR' })).concat(staleRows);
  const paginate = (rows) => rows.slice(page * perPage, page * perPage + perPage);
  const hasIssues = allRows.length > 0;
  const { val, unit } = displayDuration(lastIngestDelta, 'millis');
  return (
    <>
      <Row className="bp-close-row border">
        <div className="building-name-container">
          <Typography className="bp-building-name" variant="subtitle1">
            {building.name}
          </Typography>
        </div>
        <Fill />
        <Typography className="bp-building-name percent-active" variant="subtitle1">
          {percentOnline}% Active
        </Typography>
      </Row>
      {isOffline && (
        <Row className="bp-close-row border">
          <div className="building-name-container">
            <Typography variant="subtitle1">Building is offline</Typography>
          </div>
          <Fill />
          <Typography variant="subtitle1">
            Last data ingest was {val} {unit} ago
          </Typography>
        </Row>
      )}
      {hasIssues && (
        <Row className="bp-close-row">
          <Table className="bp-points-table">
            <TableHead>
              <TableRow>
                <TableCell>Point</TableCell>
                <TableCell>Equipment</TableCell>
                <TableCell>Point Type</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginate(allRows).map((p) => (
                <PointRow
                  key={p.id}
                  point={p}
                  variant={p.variant}
                  building={building}
                  pointTypes={pointTypes}
                  getEquipment={getEquipment}
                />
              ))}
            </TableBody>
            {allRows.length > INITIAL_PER_PAGE && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    colSpan={6}
                    count={allRows.length}
                    rowsPerPage={perPage}
                    rowsPerPageOptions={[10, 25, 50]}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                      setPerPage(e.target.value);
                      setPage(0);
                    }}
                  />
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </Row>
      )}
    </>
  );
};

export default connect((state, { buildingId }) => {
  const {
    entities: {
      points: { staleIdsByBuilding, byId },
      pointTypes: { byId: pointTypes },
      buildings: { byId: buildingsById },
    },
  } = state;
  const stalePoints = (staleIdsByBuilding[buildingId] || [])
    .map((id) => byId[id])
    .filter((p) => p != null);
  const { pointsWithMessages, latestTimestamp } = getBrokenPoints(state, buildingId);
  const brokenPoints = pointsWithMessages
    .filter(([, message]) => message.timestamp >= latestTimestamp || latestTimestamp == null)
    .map(([point]) => point);
  const building = buildingsById[buildingId] || {};
  const onlineCount = Math.max(building.pointCount - stalePoints.length - brokenPoints.length, 0);
  const numPoints = Math.max(building.pointCount, 1);
  const percentOnline =
    onlineCount === numPoints ? 100 : ((100 * onlineCount) / numPoints).toFixed(1);
  const lastIngestDelta = new Date() - latestTimestamp;
  const isOffline = latestTimestamp != null && lastIngestDelta > OFFLINE_INGEST_CUTOFF_MS;
  return {
    stalePoints,
    brokenPoints,
    percentOnline,
    pointTypes,
    building,
    isOffline,
    lastIngestDelta,
    getEquipment: (eid) => getEquipFromState(state, eid),
  };
})(BuildingPoints);
